"use client";

import React, {useState} from "react";
import {usePathname} from "next/navigation";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowRight} from "@fortawesome/free-solid-svg-icons";
import {NavigationLink} from "~/components/navigation/Link";
import {
    isNavigationItemActive,
    isNavigationItemCurrent,
    NavigationItem,
} from "@/utils/content/navigation";
import {Locale} from "~/i18n-config";
import clsx from "clsx";

const MAIN_NAVIGATION_OMITTED_QUERY_PARAMS = ["page"] as const;
const MOBILE_NAVIGATION_NESTED_INDENT = "1.25rem";

export function HeaderMain(
    { lang, navigation }: { lang: Locale, navigation: NavigationItem[] }
) {
    return (
        <nav className="header header-main-desktop hidden lg:flex gap-4 pr-10">
            <HeaderMainNavigation
                lang={lang}
                navigation={navigation}
                visible={true}
                level={0} />
        </nav>
    );
}

export function HeaderMainNavigation(
    { lang, navigation, level, visible }: { lang: Locale, navigation: NavigationItem[], level: number, visible: boolean }
) {
    return (
        <ul className={clsx(
            level === 0 || visible ? "block gap-10" : "hidden",
            level > 0 && "z-999",
            `nav level-${level}`,
        )}>
            {navigation.map((item: NavigationItem) => {
                return (
                    <HeaderMainNavigationItem
                        key={`hmni-${item.id}-${level}`}
                        lang={lang}
                        item={item}
                        level={level}
                    />
                );
            })}
        </ul>
    );
}

export function HeaderMainNavigationItem(
    { lang, item, level }: { lang: Locale, item: NavigationItem, level: number }
) {
    const pathname = usePathname();
    const [menuState, setMenuState] = useState({
        pathname,
        visible: false,
    });
    const showMenu = menuState.pathname === pathname && menuState.visible;
    const active = isNavigationItemActive(item, pathname);
    const current = isNavigationItemCurrent(item, pathname);

    return (
        <li
            onMouseEnter={() => setMenuState({pathname, visible: true})}
            onMouseLeave={() => setMenuState({pathname, visible: false})}
        >
            <NavigationLink
                lang={lang}
                item={item}
                active={active}
                current={current}
                omittedQueryParams={MAIN_NAVIGATION_OMITTED_QUERY_PARAMS}
                className={clsx(
                    "text-lg font-normal no-underline",
                    active && level === 0 && "font-semibold!",
                    active && level > 0 && "bg-white/80 text-zinc-800!",
                )}
            />
            {item.children && item.children.length > 0 && (
                <HeaderMainNavigation
                    lang={lang}
                    navigation={item.children}
                    level={level + 1}
                    visible={showMenu}
                />
            )}
        </li>
    );
}

export function HeaderMainMobile(
    { lang, navigation }: { lang: Locale, navigation: NavigationItem[] }
) {
    return (
        <nav className="header header-main-mobile px-6 mt-4 py-3 space-y-2">
            <HeaderMainMobileNavigation lang={lang} navigation={navigation} level={0} />
        </nav>
    );
}

export function HeaderMainMobileNavigation(
    { lang, navigation, level }: { lang: Locale, navigation: NavigationItem[], level: number }
) {
    return (
        <ul
            className={`nav level-${level}`}
            style={{
                paddingInlineStart: level > 0
                    ? MOBILE_NAVIGATION_NESTED_INDENT
                    : undefined,
            }}
        >
            {navigation.map((item: NavigationItem) => {
                return (
                    <HeaderMainMobileNavigationItem
                        key={`hmmni-mobile-${item.id}-${level}`}
                        lang={lang}
                        item={item}
                        level={level}
                    />
                );
            })}
        </ul>
    );
}

export function HeaderMainMobileNavigationItem(
    { lang, item, level }: { lang: Locale, item: NavigationItem, level: number }
) {
    const pathname = usePathname();
    const active = isNavigationItemActive(item, pathname);
    const current = isNavigationItemCurrent(item, pathname);

    return (
        <li>
            <NavigationLink
                lang={lang}
                item={item}
                active={active}
                current={current}
                omittedQueryParams={MAIN_NAVIGATION_OMITTED_QUERY_PARAMS}
                append={item.linkType !== "No Link" ? (
                    <FontAwesomeIcon
                        size="lg"
                        color="#000000"
                        icon={faArrowRight}
                        className="relative invisible pl-2 opacity-0 group-hover:visible group-hover:animate-bounceRight group-hover:opacity-100 group-focus-visible:visible group-focus-visible:animate-bounceRight group-focus-visible:opacity-100"
                    />
                ) : undefined}
                className={clsx(
                    "group text-lg text-zinc-800 no-underline",
                    level === 0 ? "font-bold!" : "font-normal",
                    active && level > 0 && "font-semibold!",
                    active && level > 0 && "text-grass-700!",
                )}
            />
            {item.children && item.children.length > 0 && (
                <HeaderMainMobileNavigation
                    lang={lang}
                    navigation={item.children}
                    level={level + 1}
                />
            )}
        </li>
    );
}
